/**
 * Patient Edit Page
 *
 * 患者編集 - Patient Edit/Create Screen
 * Converted from RA2 Laravel/Blade system
 *
 * Routes:
 * - /:tenant/patients/edit (Create new patient)
 * - /:tenant/patients/copy/:copyFromId (New patient pre-filled from existing; RA2 copyInit)
 * - /:tenant/patients/edit/:id (Edit existing patient)
 *
 * A2-style history context (optional deep link / refresh), same query keys as legacy:
 * ?patient_information_id=&health_insurance_card_id=&medical_aid_id=&type=all_change
 */

import { AppButton, AppTextField } from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import React, { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SubmitErrorHandler } from 'react-hook-form';

import { AppCheckbox, AppModal } from '@/components/atoms';
import { AddressSearchModal, InsurerSearchModal, RA2NDatePicker } from '@/components/organisms';
import HealthInsuranceSection from '@pages/main/patient/components/HealthInsuranceSection';
import KoreiModal from '@pages/main/patient/components/KoreiModal';
import MedicalAidSearchModal from '@pages/main/patient/components/MedicalAidSearchModal';
import MedicalAidSection from '@pages/main/patient/components/MedicalAidSection';
import OtherInfoSection from '@pages/main/patient/components/OtherInfoSection';
import PatientInfoSection from '@pages/main/patient/components/PatientInfoSection';
import PatientNavigationButtons, {
  getTreatmentFlowContextFromPatient,
  PatientPdfTemplatesDropdown,
  selectPdfTemplateItems,
} from '@pages/main/patient/components/PatientNavigationButtons';
import PatientSearchModal from '@pages/main/patient/components/PatientSearchModal';
import { usePatientEditPage } from '@pages/main/patient/hooks';
import type { PatientFormSchemaType } from '@pages/main/patient/types';
import {
  shouldShowA2BulkHistoryCreationButton,
  validateA2BulkHistoryQualificationDate,
} from '@pages/main/patient/utils/patientHistoryHelpers';
import { PATIENT_EDIT_HISTORY_SEARCH } from '@pages/main/patient/utils/patientEditHistoryUrl';
import { computePatientEditSectionLockdown } from '@pages/main/patient/utils/patientEditSectionLockdown';
import useAppToast from '@/hooks/useAppToast';
import { dateValueToIso } from '@/utils/dateConverter';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { NotFound } from '@/pages/main/system/NotFound';
import { getBulkHistoryButtonState } from './patientEditBulkHistoryButtonState';

// ==================== Sub-Components ====================
interface HeaderProps {
  patientData: ReturnType<typeof usePatientEditPage>['patientData'];
  searchKeyword: string;
  setSearchKeyword: (value: string) => void;
  includeExcluded: boolean;
  setIncludeExcluded: (value: boolean) => void;
  onPatientSearch: () => void;
  onClearForm: () => void;
  isEditMode: boolean;
  id?: string;
  tenant?: string;
  navigate: ReturnType<typeof usePatientEditPage>['navigate'];
}

const PatientEditHeader: React.FC<HeaderProps> = ({
  patientData,
  searchKeyword,
  setSearchKeyword,
  includeExcluded,
  setIncludeExcluded,
  onPatientSearch,
  onClearForm,
  isEditMode,
  id,
  tenant,
  navigate,
}) => (
  <div className="flex flex-col gap-3 mb-4 xl:flex-row xl:items-center xl:justify-between">
    {/* Left side - Patient number and search */}
    <div className="flex flex-col min-w-0 gap-3 sm:flex-row sm:items-center sm:gap-4">
      <h5
        className="m-0 shrink-0 whitespace-nowrap"
        style={{ fontWeight: '300', fontSize: '20px', color: '#212529' }}
      >
        受診者番号:
        {isEditMode &&
          patientData?.serial_number &&
          Number(patientData?.serial_number) > 0 &&
          patientData.serial_number}
        {!!patientData?.id && !patientData?.official && (
          <span
            className="text-white rounded "
            style={{ backgroundColor: '#ff8800', padding: '3.75px 6px', fontSize: '15px' }}
          >
            仮
          </span>
        )}
      </h5>
      <div className="flex flex-wrap items-center min-w-0 gap-2">
        <AppTextField
          value={searchKeyword}
          onChange={e => setSearchKeyword(e.target.value)}
          placeholder="受診者名"
          className="w-40 max-w-full"
        />
        <AppButton type="primary" onClick={onPatientSearch}>
          <i className="fa fa-search" aria-hidden="true" /> 検索
        </AppButton>
        <AppButton
          type="default"
          onClick={() => {
            onClearForm();
            setSearchKeyword('');
            setIncludeExcluded(false);
          }}
        >
          入力クリア
        </AppButton>
        <AppCheckbox checked={includeExcluded} onChange={e => setIncludeExcluded(e.target.checked)}>
          除外を含む
        </AppCheckbox>
      </div>
    </div>

    {/* Right side - Navigation buttons (edit mode) or extra actions (create mode) */}
    {isEditMode && patientData?.id ? (
      <PatientNavigationButtons
        id={id!}
        tenant={tenant}
        navigate={navigate}
        activeKey="patient-edit"
        treatmentFlowContext={getTreatmentFlowContextFromPatient(patientData)}
      />
    ) : (
      <RightSideCreateActions tenant={tenant} navigate={navigate} patientId={patientData?.id} />
    )}
  </div>
);

interface ActionBarProps {
  isEditMode: boolean;
  patientData: ReturnType<typeof usePatientEditPage>['patientData'];
  isSubmitting: boolean;
  submittingAction: ReturnType<typeof usePatientEditPage>['submittingAction'];
  onDelete: () => void;
  onProvisionalSave: () => void;
  onSubmit: () => void; // Combined submit handler
  navigate: ReturnType<typeof usePatientEditPage>['navigate'];
  id?: string;
  tenant?: string;
  colors: { sectionHeader: string; secondary: string };
}

const PatientEditActionBar: React.FC<ActionBarProps> = ({
  isEditMode,
  patientData,
  isSubmitting,
  submittingAction,
  onDelete,
  onProvisionalSave,
  onSubmit,
  navigate,
  id,
  tenant,
  colors,
}) => {
  const sidebarOpen = useSidebarOpen();
  // Compose the generic ActionBar with page-specific content
  const left = (
    <div className="flex gap-2">
      {isEditMode && patientData?.id && (
        <>
          <AppButton
            type="default"
            className="rounded"
            onClick={() => navigate(`/${tenant}/patients/${id}/histories`)}
          >
            受診者情報履歴
          </AppButton>
          <AppButton
            type="default"
            onClick={() =>
              tenant && id ? navigate(`/${tenant}/patients/${id}/dminfo/edit`) : null
            }
            className="rounded"
          >
            DM情報編集
          </AppButton>
        </>
      )}
    </div>
  );

  const right = (
    <div className="flex gap-4">
      {isEditMode && patientData?.id && (
        <AppButton
          type="default"
          onClick={onDelete}
          loading={submittingAction === 'delete'}
          disabled={isSubmitting && submittingAction !== 'delete'}
          icon={<i className="mr-2 fa fa-trash" aria-hidden="true" />}
          className="rounded font-medium !border-blue-400 !bg-blue-400 !text-white disabled:!cursor-not-allowed disabled:!border-blue-500 disabled:!bg-blue-500 disabled:!text-black/25"
        >
          削除
        </AppButton>
      )}

      <AppButton
        type="default"
        onClick={onProvisionalSave}
        loading={submittingAction === 'provisional'}
        disabled={
          Boolean(patientData?.official) || (isSubmitting && submittingAction !== 'provisional')
        }
        icon={<i className="mr-2 fa fa-paper-plane" aria-hidden="true" />}
        className="rounded !border-[#5cb85c] !bg-[#5cb85c] !text-white disabled:!cursor-not-allowed disabled:!border-[#5cb85c] disabled:!bg-[rgb(61,155,61)] disabled:!text-black/25"
      >
        仮登録
      </AppButton>

      <AppButton
        type="primary"
        onClick={onSubmit}
        loading={submittingAction === 'submit'}
        disabled={isSubmitting && submittingAction !== 'submit'}
        className="px-12 py-2 font-medium text-white rounded"
        style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
      >
        <i className="mr-2 fa fa-paper-plane" aria-hidden="true" /> 登録
      </AppButton>
    </div>
  );

  return (
    <BottomBar
      variant="action"
      visible={true}
      left={left}
      right={right}
      fixed
      className={sidebarOpen ? 'left-60' : 'left-0'}
    />
  );
};

// ==================== Main Component ====================

const PatientEditPage: React.FC = () => {
  const { colors } = useTheme();
  const toast = useAppToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Refs for section validation
  const healthInsuranceSectionRef = useRef<{
    validateSection: (formData: PatientFormSchemaType) => boolean;
    clearErrors: () => void;
    focusCardNumber: () => void;
    focusCardMark: () => void;
  }>(null);

  // Focused by AddressSearchModal after patient-address autofill.
  const patientAddress2InputRef = useRef<{ focus?: () => void } | null>(null);
  const insuranceAddress2InputRef = useRef<{ focus?: () => void } | null>(null);

  // Focused by InsurerSearchModal after 保険者 selection (記号 or 番号 depending on 保険種別).
  const insurerSearchFocusRef = useRef<{ focus?: () => void } | null>(null);

  // State for the shared history date modal (used by both patient-info validity and bulk-history flows)
  const [historyDateModalDate, setHistoryDateModalDate] = React.useState<string>('');
  const [historyDateModalError, setHistoryDateModalError] = React.useState<string>('');

  const {
    // State
    isEditMode,
    isLoading,
    hasLoadError,
    isSubmitting,
    submittingAction,
    patientData,
    searchKeyword,
    includeExcluded,
    isPatientSearchModalOpen,
    // Modal states
    isAddressSearchModalOpen,
    isInsuranceAddressSearchModalOpen,
    isInsurerSearchModalOpen,
    pendingInsurerResults,
    isKoreiModalOpen,
    isMedicalAidSearchModalOpen,
    pendingMedicalAidIntervalRows,
    isMedicalAidDirectSearchLoading,
    // Patient info validity date modal
    isPatientInfoValidityModalOpen,
    // Medical aid expiration
    isMedicalAidExpired,

    // Form
    form,
    initialFormValuesForGuard,

    // State setters
    setSearchKeyword,
    setIncludeExcluded,

    // Handlers
    onSubmit,
    handleProvisionalSave,
    handleDelete,
    handleHistorySwitch,
    handleHistoryDelete,
    handleBulkCreateHistory,
    // Bulk history date modal
    isBulkHistoryDateModalOpen,
    handleBulkHistoryDateConfirm,
    handleBulkHistoryDateModalClose,
    handlePatientSearch,
    handlePatientSearchClose,
    handlePatientSearchSelect,
    handleAddressSearch,
    handleAddressSearchClose,
    handleAddressSearchSelect,
    handleOpenMap,
    handleInsuranceAddressSearch,
    handleInsuranceAddressSearchClose,
    handleInsuranceAddressSearchSelect,
    handleInsuranceOpenMap,
    handleInsurerSearch,
    isInsurerSearchLoading,
    waitForPendingInsurerSearch,
    handleInsurerSearchClose,
    handleInsurerSearchSelect,
    handleKoreiModalClose,
    handleKoreiModalSelect,
    handleMedicalAidSearch,
    handleMedicalAidSearchClose,
    handleMedicalAidSearchSelect,
    handleMedicalAidNumberDirectSearch,
    setFocus,
    // Patient info validity date modal handlers
    handlePatientInfoValidityModalClose,
    handlePatientInfoValidityModalConfirm,
    handlePatientHistoryCreateCancel,
    handleClearForm,
    isNewHistoryMode,
    originalCurrentIds,

    // Navigation
    navigate,
    id,
    tenant,
  } = usePatientEditPage({
    historyUrl: { searchParams, setSearchParams },
    onInsurerSelectedNonKorei: () => insurerSearchFocusRef.current?.focus?.(),
  });

  /** A2: `patient_information_id` in query with empty value = new 受診者履歴 draft (履歴作成取消). */
  const patientInformationHistoryDraftByUrl = useMemo(() => {
    const k = PATIENT_EDIT_HISTORY_SEARCH.patient_information_id;
    if (!searchParams.has(k)) return false;
    const v = searchParams.get(k);
    return v === null || String(v).trim() === '';
  }, [searchParams]);

  /** A2: empty `health_insurance_card_id` in query = new 保険証履歴 draft (履歴作成取消). */
  const healthInsuranceHistoryDraftByUrl = useMemo(() => {
    const k = PATIENT_EDIT_HISTORY_SEARCH.health_insurance_card_id;
    if (!searchParams.has(k)) return false;
    const v = searchParams.get(k);
    return v === null || String(v).trim() === '';
  }, [searchParams]);

  /** A2: empty `medical_aid_id` in query = new 医療助成履歴 draft (履歴作成取消). */
  const medicalAidHistoryDraftByUrl = useMemo(() => {
    const k = PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id;
    if (!searchParams.has(k)) return false;
    const v = searchParams.get(k);
    return v === null || String(v).trim() === '';
  }, [searchParams]);

  /**
   * Determine if each section is displaying the original current record (is_current=true from backend).
   * After history switch, `is_current` gets overwritten by URL param logic, so we compare
   * the currently viewed record's ID against the original backend current IDs.
   */
  const isPatientInfoCurrent = useMemo(() => {
    if (!isEditMode || !patientData?.id) return true;
    const viewedId = patientData?.current_patient_information?.id;
    if (!viewedId) return true;
    return viewedId === originalCurrentIds.patientInformationId;
  }, [isEditMode, patientData, originalCurrentIds]);

  const isHealthInsuranceCurrent = useMemo(() => {
    if (!isEditMode || !patientData?.id) return true;
    const viewedId = patientData?.health_insurance_cards?.find(c => c.is_current)?.id;
    if (!viewedId) return true;
    return viewedId === originalCurrentIds.healthInsuranceCardId;
  }, [isEditMode, patientData, originalCurrentIds]);

  const isMedicalAidCurrent = useMemo(() => {
    if (!isEditMode || !patientData?.id) return true;
    const viewedId = patientData?.medical_aids?.find(a => a.is_current)?.id;
    if (!viewedId) return true;
    return viewedId === originalCurrentIds.medicalAidId;
  }, [isEditMode, patientData, originalCurrentIds]);

  const watchedFormValuesForGuard = form.watch();
  const { clearFormChange, hasFormChanged, confirmIfChanged } = useFormChangeGuard(
    watchedFormValuesForGuard,
    initialFormValuesForGuard ?? undefined
  );

  // Section lockdown logic: if ONE section is a new history draft (and not bulk), disable the others.
  // (HIT-4723) An empty medical_aid_id for a patient with no 医療助成 is "no MA", not a draft —
  // see computePatientEditSectionLockdown.
  const { disablePatientInfo, disableInsurance, disableMedicalAid, disableOther } =
    computePatientEditSectionLockdown({
      patientInformationHistoryDraftByUrl,
      healthInsuranceHistoryDraftByUrl,
      medicalAidHistoryDraftByUrl,
      medicalAidInsurerTypeId: form.watch('medical_aid.insurer_type_id'),
      medicalAidRecordCount: patientData?.medical_aids?.length ?? 0,
    });

  const handleClearFormWithValidation = useCallback(() => {
    // Use confirmIfChanged to show warning if form has changes
    confirmIfChanged(() => {
      // Clear health insurance section validation errors
      if (healthInsuranceSectionRef.current) {
        healthInsuranceSectionRef.current.clearErrors();
      }

      // Reset form data directly to ensure blocker doesn't see stale changes
      form.reset(initialFormValuesForGuard || undefined, {
        keepDirty: false,
        keepTouched: false,
      });

      // Clear global form change state
      clearFormChange();

      // Authorize the next navigation to skip blocker confirmation
      authorizeNavigation();

      // Call the original clear form handler after state is updated
      handleClearForm();
    });
  }, [
    handleClearForm,
    clearFormChange,
    hasFormChanged,
    confirmIfChanged,
    initialFormValuesForGuard,
    form,
  ]);

  // 保険者検索 → select → focus 記号 (or 番号 for 後期高齢). Skip when Korei opens on 39-prefix
  // since Korei's own onSelect handles focus.
  insurerSearchFocusRef.current = {
    focus: () => {
      if (isKoreiModalOpen) return;
      const insuranceTypeId = form.watch('health_insurance_card.insurance_type_id');
      if (insuranceTypeId === 2) {
        healthInsuranceSectionRef.current?.focusCardNumber();
      } else {
        healthInsuranceSectionRef.current?.focusCardMark();
      }
    },
  };

  // Custom submit handler: history range overlap (BD) + health insurance section modal rules
  const handleFormSubmit = useCallback(
    async (data: PatientFormSchemaType) => {
      form.clearErrors([
        'health_insurance_card.qualification_date',
        'health_insurance_card.expiration_date',
        'medical_aid.application_start_date',
        'medical_aid.application_end_date',
      ]);

      const bulkQual =
        data._bulkHistoryQualificationDate ??
        (isNewHistoryMode ? data.health_insurance_card?.qualification_date : undefined);
      if (bulkQual) {
        const a2Bulk = validateA2BulkHistoryQualificationDate(String(bulkQual), patientData);
        if (!a2Bulk.ok) {
          form.setError('health_insurance_card.qualification_date', {
            type: 'manual',
            message: a2Bulk.message,
          });
          toast.error('更新ができませんでした。');
          return;
        }
      }

      if (healthInsuranceSectionRef.current) {
        const isValid = healthInsuranceSectionRef.current.validateSection(data);
        if (!isValid) {
          toast.error('更新ができませんでした。');
          return;
        }
      }

      try {
        await onSubmit(data);
        clearFormChange();
      } catch (error) {
        throw error;
      }
    },
    [form, onSubmit, toast, clearFormChange, patientData, isNewHistoryMode]
  );

  const handleFormSubmitInvalid = useCallback<SubmitErrorHandler<PatientFormSchemaType>>(
    formErrors => {
      const dobError = (
        formErrors as { patient_information?: { date_of_birth?: { toast?: string } } }
      )?.patient_information?.date_of_birth?.toast;
      const FUTURE_MSG = '入力された生年月日に誤りがあります。確認後、訂正して登録してください。';
      if (dobError === FUTURE_MSG) {
        toast.error(FUTURE_MSG);
        return;
      }
      toast.error('更新ができませんでした。');
    },
    [toast]
  );

  const handleRegisterClickWithLogging = useCallback(async () => {
    // Wait for any in-flight insurer search triggered by blur before validating
    await waitForPendingInsurerSearch();
    void form.handleSubmit(handleFormSubmit, handleFormSubmitInvalid)();
  }, [form, handleFormSubmit, handleFormSubmitInvalid, waitForPendingInsurerSearch]);

  // Provisional save handler
  const handleProvisionalSaveWithLogging = useCallback(async () => {
    try {
      await handleProvisionalSave();
      clearFormChange();
    } catch (error) {
      throw error;
    }
  }, [form, handleProvisionalSave, clearFormChange, isEditMode, patientData?.id]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  if (hasLoadError && !patientData) {
    return <NotFound />;
  }

  const bulkHistoryButtonState = getBulkHistoryButtonState(isSubmitting, submittingAction);

  return (
    <div className="pb-4 patient-edit-page">
      <PatientEditHeader
        patientData={patientData}
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        includeExcluded={includeExcluded}
        setIncludeExcluded={setIncludeExcluded}
        onPatientSearch={handlePatientSearch}
        onClearForm={handleClearFormWithValidation}
        isEditMode={isEditMode}
        id={id}
        tenant={tenant}
        navigate={navigate}
      />

      {/* Form Sections */}
      <form
        onSubmit={handleSubmit(handleFormSubmit, handleFormSubmitInvalid)}
        className="space-y-4 pb-28"
      >
        {/* Bulk History Creation Button - shown when there are existing records */}
        {isEditMode &&
          patientData?.id &&
          (isNewHistoryMode || shouldShowA2BulkHistoryCreationButton(isEditMode, patientData)) && (
            <div className="flex justify-start">
              {isNewHistoryMode ? (
                <AppButton
                  type="default"
                  onClick={() => void handlePatientHistoryCreateCancel()}
                  loading={bulkHistoryButtonState.loading}
                  disabled={bulkHistoryButtonState.disabled}
                  className="px-4 py-2 !bg-amber-500 !border-amber-500 !text-white hover:!bg-amber-600"
                >
                  まとめて履歴作成取消
                </AppButton>
              ) : (
                <AppButton
                  type="default"
                  onClick={handleBulkCreateHistory}
                  loading={bulkHistoryButtonState.loading}
                  disabled={bulkHistoryButtonState.disabled}
                  className="px-4 py-2"
                >
                  まとめて履歴作成
                </AppButton>
              )}
            </div>
          )}
        {/* Patient Information Section */}
        <PatientInfoSection
          register={register}
          control={form.control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          isEditMode={isEditMode}
          isCurrent={isPatientInfoCurrent}
          onAddressSearch={handleAddressSearch}
          onOpenMap={handleOpenMap}
          patientData={patientData}
          onHistorySwitch={handleHistorySwitch}
          onHistoryDelete={handleHistoryDelete}
          onPatientHistoryCreateCancel={handlePatientHistoryCreateCancel}
          patientInformationHistoryDraftByUrl={patientInformationHistoryDraftByUrl}
          address2Ref={patientAddress2InputRef}
          disabled={disablePatientInfo}
        />

        {/* Health Insurance Card Section */}
        <HealthInsuranceSection
          ref={healthInsuranceSectionRef}
          register={register}
          control={form.control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          isEditMode={isEditMode}
          isCurrent={isHealthInsuranceCurrent}
          onInsurerSearch={handleInsurerSearch}
          isInsurerSearchLoading={isInsurerSearchLoading}
          onAddressSearch={handleInsuranceAddressSearch}
          onOpenMap={handleInsuranceOpenMap}
          patientData={patientData}
          onHistorySwitch={handleHistorySwitch}
          onHistoryDelete={handleHistoryDelete}
          onHealthInsuranceHistoryCreateCancel={handlePatientHistoryCreateCancel}
          healthInsuranceHistoryDraftByUrl={healthInsuranceHistoryDraftByUrl}
          address2Ref={insuranceAddress2InputRef}
          disabled={disableInsurance}
        />

        {/* Bottom Row - Medical Aid and Other */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <MedicalAidSection
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            isEditMode={isEditMode}
            isCurrent={isMedicalAidCurrent}
            isExpired={isMedicalAidExpired}
            onMedicalAidSearch={handleMedicalAidSearch}
            onMedicalAidNumberDirectSearch={handleMedicalAidNumberDirectSearch}
            isMedicalAidDirectSearchLoading={isMedicalAidDirectSearchLoading}
            setFocus={setFocus}
            patientData={patientData}
            onHistorySwitch={handleHistorySwitch}
            onHistoryDelete={handleHistoryDelete}
            onMedicalAidHistoryCreateCancel={handlePatientHistoryCreateCancel}
            medicalAidHistoryDraftByUrl={medicalAidHistoryDraftByUrl}
            disabled={disableMedicalAid}
          />

          <OtherInfoSection
            register={register}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            disabled={disableOther}
          />
        </div>
      </form>

      {/* Patient Search Modal */}
      <PatientSearchModal
        isOpen={isPatientSearchModalOpen}
        onClose={handlePatientSearchClose}
        onSelect={handlePatientSearchSelect}
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        includeExcluded={includeExcluded}
        setIncludeExcluded={setIncludeExcluded}
        tenant={tenant!}
      />

      {[
        {
          key: 'patient',
          isOpen: isAddressSearchModalOpen,
          onClose: handleAddressSearchClose,
          onSelect: handleAddressSearchSelect,
          postalCode1: form.watch('patient_information.postal_code1') || '',
          postalCode2: form.watch('patient_information.postal_code2') || '',
          address: form.watch('patient_information.address1') ?? '',
          addressType: 'patient' as const,
          title: '住所補助',
          focusAfterSelectRef: patientAddress2InputRef,
        },
        {
          key: 'insurance',
          isOpen: isInsuranceAddressSearchModalOpen,
          onClose: handleInsuranceAddressSearchClose,
          onSelect: handleInsuranceAddressSearchSelect,
          postalCode1: form.watch('health_insurance_card.postal_code1') || '',
          postalCode2: form.watch('health_insurance_card.postal_code2') || '',
          address: form.watch('health_insurance_card.address1') ?? '',
          addressType: 'insurance' as const,
          title: '保険証住所補助',
          focusAfterSelectRef: insuranceAddress2InputRef,
        },
      ].map(modal => (
        <AddressSearchModal
          key={modal.key}
          isOpen={modal.isOpen}
          onClose={modal.onClose}
          onSelect={modal.onSelect}
          initialPostalCode1={modal.postalCode1}
          initialPostalCode2={modal.postalCode2}
          initialAddress={modal.address}
          addressType={modal.addressType}
          title={modal.title}
          focusAfterSelectRef={modal.focusAfterSelectRef}
        />
      ))}

      {/* Insurer Search Modal */}
      <InsurerSearchModal
        isOpen={isInsurerSearchModalOpen}
        onClose={handleInsurerSearchClose}
        onSelect={handleInsurerSearchSelect}
        insuranceTypeId={form.watch('health_insurance_card.insurance_type_id') || 1}
        initialInsurerNumber={form.watch('health_insurance_card.insurer_number') || ''}
        initialResults={pendingInsurerResults}
        focusAfterSelectRef={insurerSearchFocusRef}
        healthInsuranceOnly
      />

      {/* Korei Modal */}
      <KoreiModal
        isOpen={isKoreiModalOpen}
        onClose={handleKoreiModalClose}
        onSelect={rate => {
          handleKoreiModalSelect(rate);
          queueMicrotask(() => healthInsuranceSectionRef.current?.focusCardNumber());
        }}
      />

      {/* Medical Aid Search Modal */}
      <MedicalAidSearchModal
        isOpen={isMedicalAidSearchModalOpen}
        onClose={handleMedicalAidSearchClose}
        onSelect={handleMedicalAidSearchSelect}
        initialInsurerTypeId={form.watch('medical_aid.insurer_type_id') ?? undefined}
        initialMedicalAidNumber={form.watch('medical_aid.medical_aid_number') || ''}
        initialIntervalRows={pendingMedicalAidIntervalRows}
      />

      {/* Shared History Date Modal (patient-info validity & bulk-history flows) */}
      <AppModal
        title={'有効期限登録'}
        isOpen={isPatientInfoValidityModalOpen || isBulkHistoryDateModalOpen}
        width={550}
        onClose={() => {
          if (isBulkHistoryDateModalOpen) handleBulkHistoryDateModalClose();
          else handlePatientInfoValidityModalClose();
          setHistoryDateModalDate('');
          setHistoryDateModalError('');
        }}
        onOk={async () => {
          if (!historyDateModalDate) {
            setHistoryDateModalError('資格取得日は必須です。');
            toast.error('更新ができませんでした。');
            return;
          }
          setHistoryDateModalError('');
          if (isBulkHistoryDateModalOpen) {
            // A2: validate date against backend before closing modal
            const error = await handleBulkHistoryDateConfirm(historyDateModalDate);
            if (error) {
              setHistoryDateModalError(error);
              return;
            }
          } else {
            const error = await handlePatientInfoValidityModalConfirm(historyDateModalDate);
            if (error) {
              setHistoryDateModalError(error);
              return;
            }
          }
          setHistoryDateModalDate('');
        }}
        okText="適用"
        cancelText="キャンセル"
      >
        <div className="p-4">
          <p className="font-bold">受診者情報が変更された日（開始日）を入力してください。</p>
          <p className="mb-4 font-bold">
            保険証切り替えの場合は保険証の資格取得日を入力してください
          </p>

          <div className="flex items-start gap-2 flex-nowrap">
            <label className="pt-2 text-sm font-medium whitespace-nowrap">保険証資格取得日</label>
            <div className="flex flex-col gap-1">
              <RA2NDatePicker
                value={historyDateModalDate}
                onChange={val => {
                  setHistoryDateModalDate(dateValueToIso(val) ?? '');
                  if (historyDateModalError) setHistoryDateModalError('');
                }}
                className="w-40"
                placeholder="元号00年00月00日"
                error={!!historyDateModalError}
              />
              {historyDateModalError && (
                <span className="text-xs text-red-500">{historyDateModalError}</span>
              )}
            </div>
          </div>
        </div>
      </AppModal>

      {/* Bottom Action Bar */}
      <PatientEditActionBar
        isEditMode={isEditMode}
        patientData={patientData}
        isSubmitting={isSubmitting}
        submittingAction={submittingAction}
        onDelete={handleDelete}
        onProvisionalSave={handleProvisionalSaveWithLogging}
        onSubmit={handleRegisterClickWithLogging}
        navigate={navigate}
        id={id}
        tenant={tenant}
        colors={colors}
      />
    </div>
  );
};

const RightSideCreateActions: React.FC<{
  tenant?: string;
  navigate: ReturnType<typeof usePatientEditPage>['navigate'];
  patientId?: number | string | undefined;
}> = ({ tenant, navigate, patientId }) => {
  const { serviceType: themeServiceType } = useTheme();
  const templateItems = selectPdfTemplateItems(themeServiceType, 'full');
  const pdfPatientId = patientId != null && patientId !== '' ? String(patientId) : '0';

  return (
    <div className="flex items-center gap-2">
      <AppButton
        type="default"
        onClick={() => navigate(`${tenant ? `/${tenant}` : ''}/visits/schedule`)}
      >
        訪問
      </AppButton>
      <PatientPdfTemplatesDropdown patientId={pdfPatientId} items={templateItems} />
    </div>
  );
};

export default PatientEditPage;
